import FFmpegService from "@/services/media/FFmpegService";

export default class AudioExtractor {

    private ffmpeg: FFmpegService = new FFmpegService();
    private loaded = false;
    private isRunning = false;

    constructor(private loggingEnabled = false) {}

    public running() {
        return this.isRunning;
    }

    public async extractAudio(
        file: File,
        signal: AbortSignal,
        onProgress: (progress: number, secondsLeft: number) => void
    ): Promise<{data: ArrayBuffer, extension: string}> {
        if (this.isRunning) {
            throw new Error('An extraction is already in progress.');
        }
        const inputDir = '/input';
        const inputFile = `${inputDir}/${file.name}`;
        this.isRunning = true;
        await this.waitForLoad();

        await this.ffmpeg.createDir(inputDir);
        // @ts-ignore
        await this.ffmpeg.mount('WORKERFS', {files: [file]}, inputDir);

        const abortHandler = () => {
            this.ffmpeg.terminate();
            this.loaded = false;
        };
        signal.addEventListener('abort', abortHandler);

        const metadata = await this.ffmpeg.getMediaMetaData(inputFile);
        const totalDuration = metadata.videos[0]?.duration || 0;
        if (!totalDuration) {
            throw new Error('Invalid media file');
        }

        const startedAt = Date.now();
        const audioCodec = metadata.audio[0]?.codec || '';
        const {format, extension, encode} = this.outputFormat(audioCodec);
        const outputFile = `/output.${extension}`;

        const onLog = (e: any) => {
            if(this.loggingEnabled) {
                console.log(e.message)
            }
        };

        const progressHandler = (e: any) => {
            if (!signal.aborted && e.progress <= 1 && e.progress > 0) {
                const deltaSeconds = (Date.now() - startedAt) / 1000;
                const avgSpeed = e.progress / deltaSeconds;
                const secondsLeft = (1 - e.progress) / avgSpeed;
                onProgress(e.progress, secondsLeft);
            }
        };

        this.ffmpeg.on('progress', progressHandler);
        this.ffmpeg.on('log', onLog);

        try {
            await this.ffmpeg.exec([
                "-i", inputFile,
                "-vn",
                "-c:a", encode ?? "copy",
                "-f", format,
                outputFile
            ]);
            const data = await this.ffmpeg.readFile(outputFile) as Uint8Array;
            if(!data.byteLength) {
                throw new Error('Failed to extract audio');
            }
            return {data: data.buffer, extension};
        } catch (e: any) {
            if (e.message?.includes('called FFmpeg.terminate')) {
                throw new DOMException('The operation was aborted.', 'AbortError');
            }
            this.ffmpeg.terminate();
            this.loaded = false;
            throw e;
        } finally {
            this.ffmpeg.off('progress', progressHandler);
            this.ffmpeg.off('log', onLog);
            try { await this.ffmpeg.deleteFile(outputFile); } catch {}
            try {
                await this.ffmpeg.unmount(inputDir);
                await this.ffmpeg.deleteDir(inputDir);
            } catch {}
            signal.removeEventListener('abort', abortHandler);
            this.isRunning = false;
        }
    }

    private outputFormat(codec: string): {format: string, extension: string, encode?: string} {
        switch (codec) {
            case 'opus':
            case 'vorbis':
                return {format: 'matroska', extension: 'mka'};
            case 'mp3':
                return {format: 'mp3', extension: 'mp3'};
            case 'pcm_bluray':
            case 'pcm_dvd':
            case 'pcm_s16be':
            case 'pcm_s24be':
            case 'pcm_s16le':
            case 'pcm_s24le':
                return {format: 'wav', extension: 'wav', encode: 'pcm_s16le'};
            default:
                return {format: 'mp4', extension: 'm4a'};
        }
    }

    public async waitForLoad() {
        if (!this.loaded) {
            await this.load();
            this.loaded = true;
        }
    }

    private async load() {
        await this.ffmpeg.load();
    }
}
